import { useContext, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { PlusCircle } from 'react-feather';
import styled from '@emotion/styled';
import type { QuickChartData, WidgetInstance } from '@trinity/chat';
import { WidgetSourceType } from '@trinity/chat';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { ChartSaveModal } from '@trinity/chat-components/components/WidgetGrid/ChartSaveModal';
import { WidgetPlacementModal } from '@trinity/chat-components/components/WidgetGrid/WidgetPlacementModal';
import {
  ChartWidgetApiContext,
  type ChartWidgetApiContextValue,
} from '@trinity/chat-components/containers/ChartWidget/Context';
import { useCurrentSegmentIdsContext } from '@trinity/chat-components/containers/Segmentation.Dashboard/context';
import { chatDataToQuickChartData } from '@trinity/chat-components/utils/chart';
import { CapabilitiesContext } from '../BrandInsights/context';
import { ChartInfoContext, ChartThemeIdContext } from './context';

export function AddToDashboardButton({
  quickChartData: quickChartDataProp,
  chartTitle: chartTitleProp,
}: { quickChartData?: QuickChartData; chartTitle?: string } = {}): React.ReactNode {
  const chartWidgetApi = useContext(ChartWidgetApiContext);
  const chartInfoState = useContext(ChartInfoContext);
  const chartThemeState = useContext(ChartThemeIdContext);
  const capabilities = useContext(CapabilitiesContext);
  const { projectId } = useCurrentSegmentIdsContext();

  const chart = chartInfoState?.[0];
  const selectedThemeId = chartThemeState?.[0] ?? null;

  const quickChartData = useMemo(() => {
    if (quickChartDataProp) return quickChartDataProp;
    if (!chart) return null;
    return chatDataToQuickChartData(chart);
  }, [quickChartDataProp, chart]);

  const chartTitle = chartTitleProp ?? chart?.chart?.chart?.title ?? '';

  // No provider in tree → hide button (no-op for apps/chat)
  if (!chartWidgetApi) return null;
  if (!quickChartData) return null;
  if (capabilities.canAddToDashboard === false) return null;

  return (
    <AddToDashboardControl
      api={chartWidgetApi}
      quickChartData={quickChartData}
      chartTitle={chartTitle}
      selectedThemeId={selectedThemeId}
      projectId={projectId}
    />
  );
}

type ControlProps = {
  api: ChartWidgetApiContextValue;
  quickChartData: QuickChartData;
  chartTitle: string;
  selectedThemeId: number | null;
  projectId: number;
};

// Rendered only when the ChartWidget API is present, so `api.useGetSegments`
// (a hook) runs unconditionally. Hides the button unless the project has at
// least one segment dashboard to place the widget into.
function AddToDashboardControl({
  api,
  quickChartData,
  chartTitle,
  selectedThemeId,
  projectId,
}: ControlProps): React.ReactNode {
  const [saveOpen, setSaveOpen] = useState(false);
  const [placementModalOpen, setPlacementModalOpen] = useState(false);
  const [savedWidget, setSavedWidget] = useState<WidgetInstance | null>(null);

  const segmentsQuery = api.useGetSegments(projectId);

  const handleClick = useCallback(() => {
    setSaveOpen(true);
  }, []);

  const handleSaveSuccess = useCallback((newWidget: WidgetInstance) => {
    setSavedWidget(newWidget);
    setPlacementModalOpen(true);
  }, []);

  const closePlacementModal = useCallback(() => {
    setPlacementModalOpen(false);
    setSavedWidget(null);
  }, []);

  // Hide when there is no segment dashboard to place into (also while the
  // query is loading, to avoid briefly showing a button that leads nowhere).
  const hasSegments = (segmentsQuery.data?.segments.length ?? 0) > 0;
  if (!hasSegments) return null;

  const showModal = saveOpen || placementModalOpen;

  return (
    <>
      <Tooltip title="Add to dashboard">
        <StyledIconButton onClick={handleClick} aria-label="Add to dashboard">
          <StyledPlusIcon />
        </StyledIconButton>
      </Tooltip>

      {showModal &&
        createPortal(
          <ModalLayer>
            <ChartSaveModal
              open={saveOpen}
              onClose={() => setSaveOpen(false)}
              chartData={quickChartData}
              chartTitle={chartTitle}
              selectedThemeId={selectedThemeId}
              sourceMetadata={{ type: WidgetSourceType.BrandInsights }}
              onSaveSuccess={handleSaveSuccess}
              disablePortal
            />

            {savedWidget && (
              <WidgetPlacementModal
                open={placementModalOpen}
                onClose={closePlacementModal}
                widget={savedWidget}
                onPlacementSuccess={closePlacementModal}
                disablePortal
              />
            )}
          </ModalLayer>,
          document.body
        )}
    </>
  );
}

const StyledIconButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
  },
  '&:hover': {
    '& svg': {
      color: theme.palette.primary.main,
    },
  },
}));

const StyledPlusIcon = styled(PlusCircle)(({ theme }) => ({
  color: theme.palette.gray.dark1,
}));

const ModalLayer = styled.div({
  position: 'relative',
  zIndex: 1301,
});
